import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import SearchableDropdown from "../../Components/Subscription/SearchableDropdown";
import TextEditor from "../../Components/Common/TextEditor";
import { getAllContentTypes, saveEmailTemplate, getEmailTemplateById } from '../../Services/EmailTemplateService';
import { getAllClientNames } from '../../Services/ClientService';
import { APPRoutes } from "../../Utils/Route";
import { isSuperAdminUser } from "../../Utils/Permissions";

const emptyForm = {
  id: null,
  accountId: null,
  clientId: null,
  contentTypeId: null,
  subject: "",
  content: "",
  statusId: 1,
};

const validationSchema = Yup.object({
  contentTypeId: Yup.string().required("Content Type is required"),
  subject: Yup.string().trim().required("Subject is required"),
  content: Yup.string().test(
    "content-not-empty",
    "Content is required",
    (value) => {
      if (!value) return false;
      const stripped = value.replace(/<[^>]+>/g, "").replace(/&nbsp;|\s|<br\s*\/?>/gi, "").trim();
      return stripped.length > 0;
    }
  ),
});

const validationSchemaWithClient = Yup.object({
  clientId: Yup.string().required("Client is required"),
  contentTypeId: Yup.string().required("Content Type is required"),
  subject: Yup.string().trim().required("Subject is required"),
  content: Yup.string().test(
    "content-not-empty",
    "Content is required",
    (value) => {
      if (!value) return false;
      const stripped = value.replace(/<[^>]+>/g, "").replace(/&nbsp;|\s|<br\s*\/?>/gi, "").trim();
      return stripped.length > 0;
    }
  ),
});

function mapApiToForm(templateData) {
  const clientId = templateData?.ClientId !== undefined
    ? templateData.ClientId === null
      ? "global"
      : templateData.ClientId
    : templateData?.AccountId === null
      ? "global"
      : templateData?.AccountId ?? null;

  return {
    id: templateData?.Id ?? templateData?.id ?? null,
    accountId: templateData?.AccountId ?? templateData?.accountId ?? null,
    clientId,
    contentTypeId: templateData?.ContentTypeId ?? templateData?.contentTypeId ?? null,
    subject: templateData?.Subject ?? templateData?.subject ?? "",
    content: templateData?.Content ?? templateData?.content ?? templateData?.Body ?? templateData?.body ?? "",
    statusId: templateData?.StatusId ?? templateData?.statusId ?? 1,
  };
}

function buildPayload(values, isSuperAdmin, accountId) {
  return {
    id: values.id || null,
    account_id: isSuperAdmin
      ? values.clientId === "global"
        ? null
        : values.clientId
      : accountId,
    content_type_id: values.contentTypeId,
    subject: values.subject,
    content: values.content,
    status_id: values.statusId || 1,
    type: values.id ? "Update" : "Create",
  };
}

export default function EmailTemplatesForm() {
  const navigate = useNavigate();
  const { accountId: accountIdParam } = useParams();
  const isEdit = accountIdParam !== undefined;
  const editAccountId = accountIdParam === "null" ? null : accountIdParam;
  
  const { token, decodedToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [contentTypesLoading, setContentTypesLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Determine if user is super admin using the token claim
  const isSuperAdmin = isSuperAdminUser(decodedToken);
  const accountId = decodedToken?.account_id;

  const currentValidationSchema = isSuperAdmin ? validationSchemaWithClient : validationSchema;

  const formik = useFormik({
    initialValues: useMemo(() => emptyForm, []),
    enableReinitialize: false,
    validationSchema: currentValidationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        const payload = buildPayload(values, isSuperAdmin, accountId);
        
        const response = await saveEmailTemplate(payload, token);

        if (response?.is_success) {
          toast.success(response?.message || "Email template saved successfully");
          navigate(APPRoutes.EMAIL_TEMPLATES || "/email-templates");
        } else if (response?.is_success === false && response?.status_code === 500) {
          toast.error("Internal Server Error. Please Contact Support Team.");
        } else {
          toast.error(response?.message || "Failed to save email template");
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message || "Failed to save email template");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Load clients list (only for super admin)
  const loadClients = async () => {
    if (!isSuperAdmin || !token) return;

    try {
      setClientsLoading(true);
      const response = await getAllClientNames(token);
      
      if (response?.is_success) {
        const clientList = response?.data ?? [];
        const formattedClients = clientList.map((client) => ({
          value: client.Id || client.id,
          label: client.OrganizationName || client.organizationName || client.name,
        }));
        const allClients = [
          { value: "global", label: "Global" },
          ...formattedClients,
        ];
        setClients(allClients);
      } else {
        console.error("Failed to load clients:", response?.message);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      toast.error("Failed to load clients list");
    } finally {
      setClientsLoading(false);
    }
  };

  // Load content types list
  const loadContentTypes = async () => {
    if (!token) return;

    try {
      setContentTypesLoading(true);
      const response = await getAllContentTypes(token);
      
      if (response?.is_success) {
        const contentTypeList = response?.data ?? [];
        const formattedTypes = contentTypeList.map((type) => ({
          value: type.ContentTypeId || type.Id || type.id,
          label: type.ContentTypeName || type.Name || type.name,
        }));
        setContentTypes(formattedTypes);
      } else {
        console.error("Failed to load content types:", response?.message);
      }
    } catch (error) {
      console.error("Error loading content types:", error);
      toast.error("Failed to load content types list");
    } finally {
      setContentTypesLoading(false);
    }
  };

  const loadTemplateForAccountAndContentType = async (contentTypeId, selectedAccountId) => {
    if (!token || !contentTypeId || selectedAccountId === undefined) return;

    try {
      setTemplateLoading(true);
      const response = await getEmailTemplateById(contentTypeId, selectedAccountId, token);

      if (response?.is_success) {
        const templateData = response?.data ?? response;
        formik.setFieldValue("id", templateData?.Id ?? templateData?.id ?? null);
        formik.setFieldValue("subject", templateData?.Subject ?? templateData?.subject ?? "");
        formik.setFieldValue("content", templateData?.Content ?? templateData?.content ?? templateData?.Body ?? templateData?.body ?? "");
        formik.setFieldValue("statusId", templateData?.StatusId ?? templateData?.statusId ?? 1);
      } else {
        formik.setFieldValue("id", null);
        formik.setFieldValue("subject", "");
        formik.setFieldValue("content", "");
        formik.setFieldValue("statusId", 1);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load email template content");
      formik.setFieldValue("id", null);
      formik.setFieldValue("subject", "");
      formik.setFieldValue("content", "");
      formik.setFieldValue("statusId", 1);
    } finally {
      setTemplateLoading(false);
    }
  };

  const getSelectedAccountId = () => {
    if (isEdit) {
      return editAccountId;
    }

    if (isSuperAdmin) {
      return formik.values.clientId === "global" ? null : formik.values.clientId;
    }

    return accountId;
  };

  const handleContentTypeChange = async (value) => {
    formik.setFieldValue("contentTypeId", value);

    const selectedAccountId = getSelectedAccountId();
    if (!value || selectedAccountId === undefined) return;

    await loadTemplateForAccountAndContentType(value, selectedAccountId);
  };

  // Load initial data
  useEffect(() => {
    if (token && decodedToken) {
      loadClients();
      loadContentTypes();
    }
  }, [token, decodedToken]);

  useEffect(() => {
    if (isEdit && isSuperAdmin) {
      const selectedClientId = editAccountId === null ? "global" : editAccountId;
      if (formik.values.clientId !== selectedClientId) {
        formik.setFieldValue("clientId", selectedClientId);
      }
    }
  }, [isEdit, isSuperAdmin, editAccountId, formik, formik.values.clientId]);

  useEffect(() => {
    const selectedAccountId = getSelectedAccountId();
    if (!isEdit && formik.values.contentTypeId && selectedAccountId !== undefined) {
      loadTemplateForAccountAndContentType(formik.values.contentTypeId, selectedAccountId);
    }
  }, [formik.values.clientId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '85vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow rounded-4">
            <div className="card-header bg-white border-0 py-3 rounded-4">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn btn-light border rounded-circle shadow-sm"
                  onClick={() => navigate(-1)}
                  style={{ width: 40, height: 40 }}
                >
                  &larr;
                </button>
                <div>
                  <h4 className="card-title fw-bold hc-text-primary mb-0">
                    {isEdit ? "Edit Email Template" : "Create Email Template"}
                  </h4>
                  <small className="text-muted">Create or update email template details</small>
                </div>
              </div>
            </div>

            <div className="card-body p-4">
              <form onSubmit={formik.handleSubmit}>
                {/* Clients and Content Types Row */}
                <div className="row mb-4 g-3">
                  {/* Client Dropdown - Only for Super Admin */}
                  {isSuperAdmin && (
                    <div className="col-12 col-lg-6">
                      <SearchableDropdown
                        label="Client"
                        placeholder="Select Client"
                        options={clients}
                        value={formik.values.clientId}
                        onChange={(value) => formik.setFieldValue("clientId", value)}
                        disabled={clientsLoading || (isEdit && isSuperAdmin)}
                        loading={clientsLoading}
                        required
                        emptyMessage="No clients found"
                      />
                      {formik.touched.clientId && formik.errors.clientId && (
                        <small className="text-danger d-block mt-1">{formik.errors.clientId}</small>
                      )}
                    </div>
                  )}

                  {/* Content Type Dropdown */}
                  <div className={isSuperAdmin ? "col-12 col-lg-6" : "col-12"}>
                    <SearchableDropdown
                      label="Content Type"
                      placeholder="Select Content Type"
                      options={contentTypes}
                      value={formik.values.contentTypeId}
                      onChange={handleContentTypeChange}
                      disabled={contentTypesLoading}
                      loading={contentTypesLoading || templateLoading}
                      required
                      emptyMessage="No content types found"
                    />
                    {formik.touched.contentTypeId && formik.errors.contentTypeId && (
                      <small className="text-danger d-block mt-1">{formik.errors.contentTypeId}</small>
                    )}
                  </div>
                </div>

                {/* Subject Field */}
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2">
                    Subject
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control hc-input ${
                      formik.touched.subject && formik.errors.subject ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email subject"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <small className="text-danger d-block mt-1">{formik.errors.subject}</small>
                  )}
                </div>

                {/* Text Editor */}
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2">
                    Content
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <div className="text-editor-skeleton-wrapper">
                    {templateLoading ? (
                      <div className="skeleton position-relative overflow-hidden rounded-2" style={{ minHeight: 300 }} />
                    ) : (
                      <TextEditor
                        content={formik.values.content}
                        setContent={(value) => formik.setFieldValue("content", value)}
                        onBlur={() => formik.setFieldTouched("content", true)}
                        isInvalid={formik.touched.content && Boolean(formik.errors.content)}
                        subject={formik.values.subject}
                      />
                    )}
                  </div>
                  {formik.touched.content && formik.errors.content && (
                    <small className="text-danger d-block mt-1">{formik.errors.content}</small>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-3 px-4"
                    onClick={() => navigate(APPRoutes.EMAIL_TEMPLATES || "/email-templates")}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn hc-bg-primary text-white rounded-3 px-4"
                    disabled={submitting || templateLoading}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : templateLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Save Template"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}